import { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot, addDoc, updateDoc, deleteDoc, doc, arrayUnion, arrayRemove, increment, writeBatch, getDoc, getDocs, collectionGroup } from 'firebase/firestore';
import { db, auth } from '../services/firebase';

// We use chrome.storage.local for both the Chrome Extension and the Tauri Desktop App (which polyfills it)
const checkIsLocal = () => {
    if (typeof window !== 'undefined') {
        if (window.__IS_WEB__) return false;
        if (window.__IS_DESKTOP__) return true;
    }
    return typeof chrome !== 'undefined' && chrome.storage && chrome.storage.local;
};

export function useSnippets(organization = null, teams = [], projectId = null) {
  const [snippets, setSnippets] = useState([]);
  const [loading, setLoading] = useState(true);

  const orgId = organization?.id || null;
  const teamsKey = teams?.map(t => t.id).join(',') || '';

  useEffect(() => {
    const isLocal = checkIsLocal();
    if (isLocal) {
      // Chrome Extension: Use local storage
      chrome.storage.local.get(['snippets'], (res) => {
        let loaded = res.snippets || [];
        if (projectId) loaded = loaded.filter(s => s.projectId === projectId);
        setSnippets(loaded);
        setLoading(false);
      });

      const listener = (changes, namespace) => {
        if (namespace === 'local' && changes.snippets) {
          let loaded = changes.snippets.newValue || [];
          if (projectId) loaded = loaded.filter(s => s.projectId === projectId);
          setSnippets(loaded);
        }
      };
      chrome.storage.onChanged.addListener(listener);

      return () => {
        chrome.storage.onChanged.removeListener(listener);
      };
    } else {
      // Web App: Use Firebase
      const unsubscribeAuth = auth.onAuthStateChanged(user => {
        if (user) {
          let personalSnippets = [];
          let orgSnippets = [];

          const mergeSnippets = () => {
            const all = [...personalSnippets, ...orgSnippets];
            // Deduplicate by ID
            const unique = Array.from(new Map(all.map(s => [s.id, s])).values());
            // Sort descending by createdAt
            unique.sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime());
            setSnippets(unique);
            setLoading(false);
          };

          let personalQ;
          if (projectId) {
            personalQ = query(collection(db, 'users', user.uid, 'snippets_metadata'), where('projectId', '==', projectId));
          } else {
            personalQ = query(collection(db, 'users', user.uid, 'snippets_metadata'));
          }

          const unsubPersonal = onSnapshot(personalQ, (snapshot) => {
            personalSnippets = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            mergeSnippets();
          });

          let unsubOrg = null;
          if (organization) {
            const orgQ = query(collectionGroup(db, 'snippets_metadata'), where('orgId', '==', organization.id));
            unsubOrg = onSnapshot(orgQ, (snapshot) => {
              const fetched = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
              // In-memory filter
              orgSnippets = fetched.filter(snippet => {
                if (projectId && snippet.projectId !== projectId) return false;
                if (snippet.visibility === 'organization') return true;
                if (snippet.visibility === 'team' && teams?.some(t => t.id === snippet.teamId)) return true;
                return false;
              });
              mergeSnippets();
            });
          }

          return () => {
            unsubPersonal();
            if (unsubOrg) unsubOrg();
          };
        } else {
          setSnippets([]);
          setLoading(false);
        }
      });
      return () => unsubscribeAuth();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, teamsKey, projectId]);

  const addSnippet = async (snippetData) => {
    const isLocal = checkIsLocal();
    if (isLocal) {
      const newId = Date.now().toString();
      const newSnippet = { ...snippetData, id: newId, createdAt: new Date().toISOString() };
      return new Promise((resolve) => {
        chrome.storage.local.get(['snippets'], (res) => {
          const currentSnippets = res.snippets || [];
          chrome.storage.local.set({ snippets: [newSnippet, ...currentSnippets] }, () => resolve(newId));
        });
      });
    }

    if (!auth.currentUser) throw new Error("Must be logged in");
    
    const batch = writeBatch(db);
    const metadataRef = doc(collection(db, 'users', auth.currentUser.uid, 'snippets_metadata'));
    const contentRef = doc(db, 'users', auth.currentUser.uid, 'snippets_content', metadataRef.id);
    
    if (snippetData.isPublic) {
      batch.update(doc(db, 'users', auth.currentUser.uid), { devItCreds: increment(1) });
    }

    const { content, gemContext, history, ...metadata } = snippetData;
    const now = new Date().toISOString();
    const contentPreview = content ? content.substring(0, 80).replace(/\n/g, ' ') + '...' : '';

    batch.set(metadataRef, {
      ...metadata,
      contentPreview,
      userId: auth.currentUser.uid,
      likes: [],
      likesCount: 0,
      createdAt: now
    });

    batch.set(contentRef, {
      content: content || '',
      gemContext: gemContext || '',
      createdAt: now
    });

    // Write first history entry if content exists
    if (content) {
      const historyRef = doc(collection(db, 'users', auth.currentUser.uid, 'snippets_content', metadataRef.id, 'history'));
      batch.set(historyRef, {
        content,
        timestamp: now
      });
    }

    await batch.commit();
    return metadataRef.id;
  };

  const updateSnippet = async (id, updates) => {
    const isLocal = checkIsLocal();
    if (isLocal) {
      return new Promise((resolve) => {
        chrome.storage.local.get(['snippets'], (res) => {
          const currentSnippets = res.snippets || [];
          const updatedSnippets = currentSnippets.map(s => s.id === id ? { ...s, ...updates, updatedAt: new Date().toISOString() } : s);
          chrome.storage.local.set({ snippets: updatedSnippets }, () => resolve());
        });
      });
    }

    if (!auth.currentUser) throw new Error("Must be logged in");
    
    const batch = writeBatch(db);
    const metadataRef = doc(db, 'users', auth.currentUser.uid, 'snippets_metadata', id);
    const contentRef = doc(db, 'users', auth.currentUser.uid, 'snippets_content', id);
    
    if (updates.isPublic === true) {
      batch.update(doc(db, 'users', auth.currentUser.uid), { devItCreds: increment(1) });
    }

    const { content, gemContext, history, ...metadataUpdates } = updates;
    const updatedAt = new Date().toISOString();

    if (content !== undefined) {
      metadataUpdates.contentPreview = content ? content.substring(0, 80).replace(/\n/g, ' ') + '...' : '';
    }

    if (Object.keys(metadataUpdates).length > 0) {
      batch.update(metadataRef, { ...metadataUpdates, updatedAt });
    }

    if (content !== undefined || gemContext !== undefined) {
      const contentUpdates = { updatedAt };
      if (content !== undefined) contentUpdates.content = content;
      if (gemContext !== undefined) contentUpdates.gemContext = gemContext;
      
      batch.set(contentRef, contentUpdates, { merge: true });

      // Add to history subcollection
      if (content !== undefined) {
        const historyRef = doc(collection(db, 'users', auth.currentUser.uid, 'snippets_content', id, 'history'));
        batch.set(historyRef, {
          content,
          timestamp: updatedAt
        });
      }
    }

    await batch.commit();
  };

  const removeSnippet = async (id) => {
    const isLocal = checkIsLocal();
    if (isLocal) {
      return new Promise((resolve) => {
        chrome.storage.local.get(['snippets'], (res) => {
          const currentSnippets = res.snippets || [];
          chrome.storage.local.set({ snippets: currentSnippets.filter(s => s.id !== id) }, () => resolve());
        });
      });
    }

    if (!auth.currentUser) throw new Error("Must be logged in");
    
    const historyRef = collection(db, 'users', auth.currentUser.uid, 'snippets_content', id, 'history');
    const historySnap = await getDocs(historyRef);

    const batch = writeBatch(db);
    batch.delete(doc(db, 'users', auth.currentUser.uid, 'snippets_metadata', id));
    batch.delete(doc(db, 'users', auth.currentUser.uid, 'snippets_content', id));
    
    historySnap.forEach(d => {
      batch.delete(d.ref);
    });

    await batch.commit();
  };

  const removeSnippets = async (ids) => {
    if (!Array.isArray(ids) || ids.length === 0) return;
    
    const isLocal = checkIsLocal();
    if (isLocal) {
      return new Promise((resolve) => {
        chrome.storage.local.get(['snippets'], (res) => {
          const currentSnippets = res.snippets || [];
          const idSet = new Set(ids);
          chrome.storage.local.set({ snippets: currentSnippets.filter(s => !idSet.has(s.id)) }, () => resolve());
        });
      });
    }

    if (!auth.currentUser) throw new Error("Must be logged in");
    
    const chunkSize = 20;
    for (let i = 0; i < ids.length; i += chunkSize) {
      const chunk = ids.slice(i, i + chunkSize);
      
      const histories = await Promise.all(
        chunk.map(id => getDocs(collection(db, 'users', auth.currentUser.uid, 'snippets_content', id, 'history')))
      );

      let batch = writeBatch(db);
      let opCount = 0;

      for (const [index, id] of chunk.entries()) {
        batch.delete(doc(db, 'users', auth.currentUser.uid, 'snippets_metadata', id));
        batch.delete(doc(db, 'users', auth.currentUser.uid, 'snippets_content', id));
        opCount += 2;

        const historySnap = histories.at(index);
        if (historySnap) {
          historySnap.forEach(d => {
            batch.delete(d.ref);
            opCount++;
          });
        }

        if (opCount >= 400) {
          await batch.commit();
          batch = writeBatch(db);
          opCount = 0;
        }
      }
      
      if (opCount > 0) {
        await batch.commit();
      }
    }
  };

  const fetchSnippetContent = async (snippetId, authorId = null) => {
    const isLocal = checkIsLocal();
    if (isLocal) {
      const s = snippets.find(x => x.id === snippetId);
      return s ? { content: s.content, gemContext: s.gemContext } : null;
    }
    if (!auth.currentUser) return null;
    
    const targetUserId = authorId || auth.currentUser.uid;
    const docRef = doc(db, 'users', targetUserId, 'snippets_content', snippetId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return docSnap.data();
    }
    return null;
  };

  const fetchPublicSnippetContent = async (snippetId, authorId) => {
    const isLocal = checkIsLocal();
    if (isLocal) {
      const s = snippets.find(x => x.id === snippetId);
      return s ? { content: s.content, gemContext: s.gemContext } : null;
    }
    const docRef = doc(db, 'users', authorId, 'snippets_content', snippetId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return docSnap.data();
    }
    return null;
  };

  const likeSnippet = async (snippetId, authorId) => {
    const isLocal = checkIsLocal();
    if (isLocal) return; // Not supported locally
    if (!auth.currentUser) throw new Error("Must be logged in");
    
    // We update likes on the metadata so it shows in the public gallery easily
    const docRef = doc(db, 'users', auth.currentUser.uid, 'snippets_metadata', snippetId);
    await updateDoc(docRef, { likes: arrayUnion(auth.currentUser.uid), likesCount: increment(1) });
  };

  const migrateData = async () => {
    if (!auth.currentUser) {
      alert("Must be logged in to migrate.");
      return;
    }
    try {
      const snippetsRef = collection(db, 'snippets');
      const q = query(snippetsRef, where("userId", "==", auth.currentUser.uid));
      const snapshot = await getDocs(q);
      
      let batch = writeBatch(db);
      let count = 0;
      let total = snapshot.docs.length;

      for (const docSnap of snapshot.docs) {
        const data = docSnap.data();
        const { content, gemContext, history, ...metadata } = data;
        
        const metadataRef = doc(db, 'users', auth.currentUser.uid, 'snippets_metadata', docSnap.id);
        const contentRef = doc(db, 'users', auth.currentUser.uid, 'snippets_content', docSnap.id);
        
        const contentPreview = content ? content.substring(0, 80).replace(/\n/g, ' ') + '...' : '';

        batch.set(metadataRef, {
          ...metadata,
          contentPreview
        });

        batch.set(contentRef, {
          content: content || '',
          gemContext: gemContext || '',
          createdAt: data.createdAt || new Date().toISOString()
        });

        // Delete the old document
        batch.delete(docSnap.ref);

        // Migrate history if exists
        if (history && Array.isArray(history)) {
          history.forEach((histItem, idx) => {
            const histRef = doc(collection(contentRef, 'history'));
            batch.set(histRef, {
              content: histItem,
              timestamp: new Date(Date.now() - idx * 1000).toISOString() // approximate order
            });
          });
        }

        count++;
        // Commit in batches of 400 (limit is 500 operations per batch)
        if (count % 100 === 0) {
          await batch.commit();
          batch = writeBatch(db);
        }
      }

      if (count % 100 !== 0) {
        await batch.commit();
      }

      alert(`Successfully migrated ${total} snippets to the new architecture!`);
      // Reload page to fetch new metadata
      window.location.reload();
    } catch (err) {
      console.error("Migration failed:", err);
      alert("Migration failed: " + err.message);
    }
  };

  const syncData = async () => {
    const isLocal = checkIsLocal();
    if (!isLocal) {
       alert("Vault synced with cloud!");
       return;
    }

    if (!auth.currentUser) {
       alert("You must be logged in to sync.");
       return;
    }

    try {
      // 1. Get all local snippets
      const localSnippets = await new Promise((resolve) => {
        chrome.storage.local.get(['snippets'], (res) => resolve(res.snippets || []));
      });

      // 2. Fetch all cloud snippets
      const cloudQ = query(collection(db, 'users', auth.currentUser.uid, 'snippets_metadata'));
      const cloudSnap = await getDocs(cloudQ);
      const cloudSnippets = cloudSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      const cloudSnippetsFull = [];
      for (const meta of cloudSnippets) {
         const contentDoc = await getDoc(doc(db, 'users', auth.currentUser.uid, 'snippets_content', meta.id));
         const contentData = contentDoc.exists() ? contentDoc.data() : { content: '', gemContext: '' };
         cloudSnippetsFull.push({ ...meta, ...contentData });
      }

      const merged = [...localSnippets];
      const localIds = new Set(localSnippets.map(s => String(s.id)));
      let uploadedCount = 0;
      let downloadedCount = 0;

      // 3. Download new from Cloud
      for (const cloud of cloudSnippetsFull) {
         if (!localIds.has(String(cloud.id))) {
            merged.push(cloud);
            downloadedCount++;
         }
      }

      // 4. Upload new from Local
      const cloudIds = new Set(cloudSnippets.map(s => String(s.id)));
      let batch = writeBatch(db);
      let opCount = 0;
      
      for (const local of localSnippets) {
         const localIdStr = String(local.id);
         if (!cloudIds.has(localIdStr)) {
            const { content, gemContext, ...metadata } = local;
            const metaRef = doc(db, 'users', auth.currentUser.uid, 'snippets_metadata', localIdStr);
            const contentRef = doc(db, 'users', auth.currentUser.uid, 'snippets_content', localIdStr);
            
            batch.set(metaRef, { ...metadata, id: localIdStr, userId: auth.currentUser.uid });
            batch.set(contentRef, { content: content || '', gemContext: gemContext || '', createdAt: metadata.createdAt || new Date().toISOString() });
            
            opCount += 2;
            uploadedCount++;
            
            if (opCount >= 400) {
               await batch.commit();
               batch = writeBatch(db);
               opCount = 0;
            }
         }
      }

      if (opCount > 0) {
         await batch.commit();
      }

      // 5. Sort merged and save locally
      merged.sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime());
      
      chrome.storage.local.set({ snippets: merged }, () => {
         alert(`Sync Complete!\nDownloaded: ${downloadedCount} snippets\nUploaded: ${uploadedCount} snippets`);
      });

    } catch (e) {
      console.error(e);
      alert("Error syncing data: " + e.message);
    }
  };

  return { snippets, loading, addSnippet, updateSnippet, removeSnippet, removeSnippets, fetchSnippetContent, fetchPublicSnippetContent, likeSnippet, migrateData, syncData };
}
